import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useFormCallbacks } from '@v2/hooks/form';
import { useHistoryWithReferrer } from '@utils/history';

import { ContentLoader } from '@components/common/presentational/loader/loader';

import { IAddress, ICardBase, ICartBox, ICoupon } from '@interfaces';
import { CART_FORM_KEY, useAddingToSubscription, useCartForm } from '@pages/cart/hooks'

import ProsList from '@v2/components/common/presentational/cart/pros-list';

import styles from '@components/pages/cart/cart.styles';
import { recalculateBoxTotals, setBoxNewOrder } from '@app/store/rtk.cart.store';
import { useCart, useCartAllBoxes, useCartBoxes } from '@app/store/cart.selectors';
import { useIsLogged } from '@app/store/rtk.user.selectors';
import checkoutGuestStyles from '../checkout/guest/checkout.guest.styles';
import { useAutoCoupon, useCalculateTotals } from '../checkout/guest/hooks';
import { Coupon, CouponForm } from '../checkout/guest/checkout.guest.coupons';
import { useCheckoutData, useCheckoutUser, useIsCheckoutLoading } from '@v2/store/rtk.checkout.selectors';
import { setCheckoutLoading } from '@v2/store/rtk.checkout.store';
import { useModal } from '@v2/hooks/modals';
import StripeCardModal from '../billing/modals/card';
import ProfileService from '@v2/services/profile.service';
import AddressModal from '../billing/modals/address';
import pencil from '@img/subscription/edit-items.svg';
import dayjs from 'dayjs';
// import PaymentModal from '../billing/modals/payment';

function OrderType() {
    const dispatch = useDispatch();
    const checkout = useCheckoutData();
    const cartForm = useCartForm();
    const boxes = useCartBoxes();

    const { updateValue } = useFormCallbacks(CART_FORM_KEY);

    function changeType(option: string) {
        boxes.forEach((box, index) => dispatch(setBoxNewOrder({ index, newOrder: option === 'create-new' })));

        if (cartForm.orderType !== option) {
            updateValue({orderType: option});
        }
    }

    const handleOrderTypeChange = (e: React.FormEvent<EventTarget>) => {
        const option = (e.target as HTMLInputElement).value;
        changeType(option);
    }

    useEffect(() => {
        if (checkout.subscription && !checkout.subscription.isActive) {
            changeType('create-new');
        }
    }, []);

    return (
        <>
            <div className="order-type">
                { checkout.subscription && checkout.subscription.isActive ?
                    <div className="option">
                        <input
                            tabIndex={0}
                            aria-label="Add to my next order"
                            type="radio"
                            id="add-to"
                            value={'add-to'}
                            name="order"
                            onChange={handleOrderTypeChange}
                            checked={'add-to' === cartForm.orderType}
                        />
                        <label className={'caption clearfix'} htmlFor="add-to">
                            {checkout.shippingNext
                                ? `Add to my next order, shipping ${dayjs(checkout.shippingNext).format('MM-DD-YYYY')}`
                                : `Add to my next order`}
                        </label>
                    </div> : null }
                <div className="option">
                    <input
                        tabIndex={0}
                        aria-label="Create a new order, processing today"
                        type="radio"
                        id="create-new"
                        value={'create-new'}
                        name="order"
                        onChange={handleOrderTypeChange}
                        checked={'create-new' === cartForm.orderType}
                    />
                    <label className={'caption clearfix'} htmlFor="create-new">
                        Create a new order, processing today
                    </label>
                </div>
            </div>
            <div className="sum-row-hr" />
        </>
    )
}

export function EditAddress({ address, entity, id, callback, useIconForEdit }: { address: IAddress, entity: string, id: number, callback?: () => void, useIconForEdit?: boolean }) {
    const { nickname, firstName, lastName, city, country,
        state, postcode, address1, address2, isDefault } = address;

    const [showAddressModal] = useModal({
        windowType: 'MEDIUM',
        alignType: 'CENTER',
        heightType: 'TALL',
        onHide: callback,
        content: {
            component: AddressModal,
            props: {
                title: 'Edit address',
                address: {
                    id: id,
                    nickname: nickname,
                    firstName: firstName,
                    lastName: lastName,
                    address1: address1,
                    address2: address2,
                    isDefault: isDefault,
                    city: city,
                    state: state,
                    postcode: postcode,
                    country: country,
                },
                entity: entity,
                callback: ProfileService.editAddress,
            },
        },
    });

    return (
        <>
            {
                useIconForEdit
                ?
                    <img className='edit-icon' onClick={() => showAddressModal()} src={pencil} height={24} alt='Edit shipping addess' />
                :
                    <a onClick={() => showAddressModal()}>Edit</a>
            }
        </>
    )
}

export default function CartOrder() {
    const dispatch = useDispatch();
    const history = useHistoryWithReferrer();
    const rendered = useRef(false);
    const rendered2 = useRef(false);
    const coupon = useAutoCoupon();
    const loading = useIsCheckoutLoading();

    const [discount, setDiscount] = useState(0);
    const [abortController, setAbortController] = useState(new AbortController());
    const [initiated, setInitiated] = useState(
        localStorage.getItem('cb_auto_coupon_applied') !== coupon
            ? false
            : true
    );

    const isLogged = useIsLogged();
    const cart = useCart();
    const boxes = useCartBoxes();
    const allboxes = useCartAllBoxes();
    const checkout = useCheckoutData();
    const user = useCheckoutUser();
    const calculateTotals = useCalculateTotals(abortController.signal);
    const addToSubscription = useAddingToSubscription();

    const isCartEmpty = Object.keys(boxes).length === 0;

    const [showStripeCardModal] = useModal({
        windowType: 'MEDIUM',
        alignType: 'CENTER',
        heightType: 'TALL',
        onHide: reload,
        content: {
            component: StripeCardModal,
            props: {
                card: {
                    firstName: '',
                    lastName: '',
                    address: '',
                    city: '',
                    state: '',
                    stripeSourceId: '',
                },
            }
        },
    });
    const [shippingAddress, setShippingAddress] = useState(null);
    const [shippingAddressStreet, setShippingAddressStreet] = useState(user?.shippingAddress.address1);
    const [payment, setPayment] = useState('');

    // const [showPaymentModal] = useModal({
    //     windowType: 'SMALL',
    //     alignType: 'CENTER',
    //     closeButtonOutside: true,
    //     onHide: reload,
    //     content: {
    //         component: PaymentModal,
    //         props: {
    //             showStripeCardModal: showStripeCardModal,
    //             card: payment ? payment : {},
    //         }
    //     },
    // });

    // this is actually the first time calculate totals is called!!!
    useEffect(() => {
        dispatch(setCheckoutLoading(true));

        // make sure to apply auto coupon only once
        if (!initiated && !isLogged && coupon) {
            calculateTotals({ newCoupon: coupon });

            setInitiated(true);

            localStorage.setItem('cb_auto_coupon_applied', coupon);

            return;
        }

        calculateTotals({});
    }, [abortController]);

    useEffect(() => {
        Object.values(allboxes).forEach((box: ICartBox, index: number) => {
            if (!box.incomplete && box.blueprint && box.blueprint.discount > 0 && box.subscribe && !box.priceDefault) {
                dispatch(recalculateBoxTotals({ index }));
            }
        });

        let boxesDiscount = 0;

        Object.values(boxes).forEach((box: ICartBox) => {
            if (box.blueprint && box.blueprint.discount > 0 && box.subscribe) {
                boxesDiscount += box.priceDefault
                                    ? box.priceDefault - box.price
                                    : ((box.price / (100 - box.blueprint.discount) * 100) - box.price)
            }
        });

        if (rendered2?.current) {
            abortController.abort();
            setAbortController(new AbortController());
        } else {
            rendered2.current = true;
        }

        setDiscount(boxesDiscount);
    }, [cart.price]);

    // --- it needs recalculating when we're logged and change stuff
    useEffect(() => {
        if (rendered.current) {
            !isLogged || calculateTotals({ noShipping: addToSubscription });
        } else {
            rendered.current = true;
        }
    }, [cart.price, addToSubscription]);

    useEffect(() => {
        (!isLogged || addToSubscription) || calculateTotals({ noShipping: addToSubscription })
    }, [checkout?.shippingMethod]);
    // ---

    const toCheckout = () => {
        if (isLogged) {
            boxes.forEach((box, index) => dispatch(setBoxNewOrder({ index, newOrder: !addToSubscription })));
        }

        history.pushWithReferrer('/checkout')
    }

    function reload() {
        ProfileService.shippingBilling()
            .then((response: any) => {
                const shippingAddress = response.shippingAddresses.find((address: any) => address.isDefault);
                const payment = response.card as ICardBase;

                setShippingAddress(shippingAddress);
                setShippingAddressStreet(shippingAddress.address1);
                if(payment.last4) {
                    setPayment(payment.type.toUpperCase() + ' ending ' + payment.last4);
                }
            });
    }

    useEffect(() => {
        if (isLogged) {
            reload();
        }
    }, [isLogged]);

    return (
        <div className={`${checkoutGuestStyles.summaryWrap} ${styles.summaryWrap}`}>
            <div className="summary-table">
                <div className="sum-row-hr mb-4 d-block d-md-none" />
                <p className="checkout-title">Order summary</p>
                {!loading && checkout?.shippingMethod === 'Standard' && (checkout?.shipping > 0 ?
                    <div className="checkout-message alert success without-icon">
                        {`Spend $${Number(45 - (checkout?.total - checkout?.shipping - checkout?.taxes + checkout?.discount)).toFixed(2)} more to get free shipping`}
                    </div> : checkout?.shipping === 0 &&
                        checkout?.cartBoxes?.length > 0 && <div className="checkout-message alert success without-icon">Congratulations! You get Free shipping</div>)
                }
                {
                    checkout?.messages && Object.values(checkout.messages).map((message: string, index: number) =>
                        <div className="checkout-message alert success without-icon" key={index}>
                            {message}
                        </div>
                    )
                }

                <div className="sum-row-hr" />

                <div className="sum-row total">
                    <div className="caption">
                        Item total
                    </div>
                    <div className="total">
                        {isCartEmpty ? '-' : `$${(cart.price + discount).toFixed(2)}`}
                    </div>
                </div>

                {discount > 0 &&
                    <div className="sum-row total coupon">
                        <div className="caption">
                            Subscription savings ({/* round(100 - (cart.price / (cart.price + discount) * 100)).toFixed(0) */}15% off)
                        </div>
                        <div className="total text-green">
                            -${discount.toFixed(2)}
                        </div>
                    </div>
                }

                {checkout && checkout.taxes && checkout.taxes > 0 ?
                    <div className="sum-row total">
                        <div className="caption">Tax</div>
                        <div className="total text-green">${checkout.taxes.toFixed(2)}</div>
                    </div> : null}

                {!addToSubscription && !loading && checkout &&
                    <div className="sum-row total coupon">
                        <div className="caption">
                            Shipping
                        </div>
                        <div className="total text-green">
                            {checkout?.shipping === 0 && checkout?.shippingPrepaid === 0
                                ? 'Free'
                                : <span className="text-blue">{
                                    (checkout.shipping || checkout.shippingPrepaid
                                        ? `$${(checkout.shipping + checkout.shippingPrepaid).toFixed(2)}`
                                        : '-')}</span>
                            }
                        </div>
                    </div>
                }

                <div className="sum-row-hr" />

                <ContentLoader loading={loading}>
                    {!addToSubscription &&
                        <>
                            {checkout?.coupons && checkout.coupons.map((coupon: ICoupon) =>
                                <div
                                    className="sum-row total coupon"
                                    key={coupon.code}>
                                    <Coupon
                                        coupon={coupon}
                                        subtotal={cart.price}
                                        remove={() => {
                                            calculateTotals({ removeCoupon: coupon.code, noShipping: addToSubscription });
                                        }} />
                                </div>
                            )}
                            <CouponForm checkout={checkout} user={user} cart={cart} />
                            <div className="sum-row-hr" />
                        </>
                    }

                    {isLogged &&
                        <OrderType />
                    }
                </ContentLoader>

                <div className="sum-row order-total">
                    <div className="caption">Order total</div>
                    {!isCartEmpty && !loading && !isNaN(checkout?.total)
                        ? <>
                            <div className={`total ${discount > 0 ? 'text-green' : ''}`}>
                                {
                                    discount > 0 &&

                                    <div className="total text-strikethrough mr-2">
                                        ${(cart.price + discount).toFixed(2)}
                                    </div>
                                }
                                ${checkout.total.toFixed(2)}
                            </div>
                        </> :
                        <div className="total">-</div>
                    }
                </div>
            </div>

            <div className="actions-wrapper">
                {!isCartEmpty
                    &&
                    <button
                        tabIndex={0}
                        disabled={loading}
                        data-acsb-clickable="true"
                        data-acsb-force-unnavigable="false"
                        data-acsb-now-navigable="true"
                        data-acsb-navigable="true"
                        type="submit"
                        style={{ width: '100%' }}
                        className={`button primary red`}
                        onClick={toCheckout}>
                        Checkout
                    </button>
                }

                { shippingAddressStreet || payment ?
                    <div className="hr" />
                    : ''
                }

                { shippingAddressStreet &&
                    <div className="line">
                        <div className='title'>Shipping</div>
                        <div className='text'>{shippingAddressStreet ? shippingAddressStreet : '-'}</div>
                        { shippingAddress ? <EditAddress callback={reload} entity='shipping' address={shippingAddress} id={shippingAddress?.id} /> : '-' }
                    </div>
                }

                { payment &&
                    <div className="line">
                        <div className='title'>Payment</div>
                        <div className='text'>{payment ? payment : '-'}</div>
                        {/* <a onClick={() => showPaymentModal()}>Edit</a> */}
                        <a onClick={() => showStripeCardModal()}>Edit</a>
                    </div>
                }

                { shippingAddressStreet || payment ?
                    <div className="hr" />
                    : ''
                }
            </div>

            <ProsList />

            <div className="tm-text tablet">Precise nutrition for your child’s brain and body ™</div>
        </div>
    );
}
